import {objectEntries} from '@jetbrains/teamcity-ui/dist/utils'
import type {
  ApiEndpointQuery,
  EndpointDefinitions,
  QueryActionCreatorResult,
  QueryArgFrom,
  QueryDefinition,
  ResultTypeFrom,
  StartQueryActionCreatorOptions,
} from '@reduxjs/toolkit/query'
import type {TypedUseQuery, TypedUseQuerySubscription} from '@reduxjs/toolkit/query/react'
import {useLoaderData} from 'react-router'

import type {AppThunk} from '../actions/types'
import type {PreloadedDataTypes} from '../components/App/App.types'

export type AnyQueryDefinition<T = any> = QueryDefinition<any, any, any, T, any>
export type Endpoint<D extends AnyQueryDefinition> = ApiEndpointQuery<D, EndpointDefinitions> & {
  useQuerySubscription: TypedUseQuerySubscription<ResultTypeFrom<D>, QueryArgFrom<D>, any>
  useQuery: TypedUseQuery<ResultTypeFrom<D>, QueryArgFrom<D>, any>
}

export type LoadedEndpoint<T, D extends AnyQueryDefinition<T> = AnyQueryDefinition<T>> = {
  endpoint: Endpoint<D>
  arg: QueryArgFrom<D>
  result: QueryActionCreatorResult<D>
}
export const loadEndpoint =
  <T, D extends AnyQueryDefinition<T>>(
    endpoint: Endpoint<D>,
    arg: QueryArgFrom<D>,
    options?: StartQueryActionCreatorOptions,
  ): AppThunk<LoadedEndpoint<T, D>> =>
  dispatch => ({
    endpoint,
    arg,
    result: dispatch(endpoint.initiate(arg, options)),
  })

function PreloadedEndpointSubscriber<T, D extends AnyQueryDefinition<T>>({
  endpoint,
  arg,
}: LoadedEndpoint<T, D>) {
  endpoint.useQuerySubscription(arg)
  return null
}

type PreloadedEndpointsSubscriberProps = {
  children: React.ReactNode
}

export type LoadedEndpoints = {
  [K in keyof PreloadedDataTypes]?:
    | LoadedEndpoint<PreloadedDataTypes[K]>
    | null
    | undefined
    | false
    | 0
    | ''
}

export type PreloadedEndpointsLoaderData = {
  preloadedEndpoints?: LoadedEndpoints
}

export function usePreloadedEndpoints(): LoadedEndpoints | undefined {
  const data = useLoaderData() as PreloadedEndpointsLoaderData | undefined
  return data?.preloadedEndpoints
}

export function PreloadedEndpointsSubscriber({children}: PreloadedEndpointsSubscriberProps) {
  const preloadedEndpoints = usePreloadedEndpoints()
  return (
    <>
      {preloadedEndpoints &&
        objectEntries(preloadedEndpoints).map(
          ([key, value]) =>
            value && <PreloadedEndpointSubscriber key={key} {...(value as LoadedEndpoint<any>)} />,
        )}
      {children}
    </>
  )
}
